import { ReactElement, useState } from "react";
import { useIntl } from "react-intl";
import { BRAND_COLOR } from "../constants";

type ImageGalleryCardProps = {
	images: string[];
	children?: ReactElement;
	isPlaceholder?: boolean;
};

export default function ImageGalleryCard({ images, children, isPlaceholder = false }: ImageGalleryCardProps) {
	const intl = useIntl();
	const [showModal, setShowModal] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);

	const total = images.length;

	const next = (e: React.MouseEvent) => {
		e.stopPropagation();
		setCurrentIndex((prev) => (prev + 1) % total);
	};

	const prev = (e: React.MouseEvent) => {
		e.stopPropagation();
		setCurrentIndex((prev) => (prev - 1 + total) % total);
	};

	return (
		<>
			<div className="card mb-3 p-2">
				<div
					className="ratio ratio-16x9 position-relative"
					onClick={() => !isPlaceholder && setShowModal(true)}
					style={{ cursor: isPlaceholder ? "default" : "pointer" }}
				>
					{isPlaceholder || total === 0 ? (
						<div
							className="d-flex justify-content-center align-items-center w-100 h-100 text-white"
							style={{ backgroundColor: BRAND_COLOR }}
						>
							{intl.formatMessage({ id: "noPhotoPlaceholder" })}
						</div>
					) : (
						<>
							<img src={images[0]} className="card-img-top object-fit-cover" alt="Preview" />
							{images.length > 1 && (
								<div>
									<span
										className="badge d-inline-flex align-items-center ms-1 text-light"
										style={{ backgroundColor: BRAND_COLOR }}
									>
										<i className="bi bi-images me-1"></i> {`+${images.length - 1}`}
									</span>
								</div>
							)}
						</>
					)}
				</div>
				{children ?? null}
			</div>

			{!isPlaceholder && showModal && (
				<div
					className="modal fade show d-block"
					tabIndex={-1}
					role="dialog"
					onClick={() => setShowModal(false)}
					style={{
						backgroundColor: "rgba(0, 0, 0, 0.85)",
						position: "fixed",
						top: 0,
						left: 0,
						width: "100%",
						height: "100%",
						zIndex: 1050,
					}}
				>
					<div className="d-flex justify-content-center align-items-center h-100 position-relative">
						<img
							src={images[currentIndex]}
							alt={`Image ${currentIndex + 1}`}
							className="img-fluid"
							style={{
								maxHeight: "90%",
								maxWidth: "90%",
								cursor: "pointer",
							}}
							onClick={(e) => e.stopPropagation()}
						/>

						{/* Image Counter */}
						<div
							className="position-absolute top-0 end-0 m-3 px-2 py-1 bg-dark text-white rounded"
							style={{ fontSize: "0.9rem" }}
						>
							<div className="text-end">
								{currentIndex + 1} / {total}
							</div>
							<div className="text-light">
								<small>{intl.formatMessage({ id: "tap_to_close" })}</small>
							</div>
						</div>

						{/* Prev button */}
						{total > 1 && (
							<button
								className="btn btn-light position-absolute start-0 top-50 translate-middle-y"
								onClick={prev}
							>
								‹
							</button>
						)}

						{/* Next button */}
						{total > 1 && (
							<button
								className="btn btn-light position-absolute end-0 top-50 translate-middle-y"
								onClick={next}
							>
								›
							</button>
						)}
					</div>
				</div>
			)}
		</>
	);
}
